/**
 * Composable for date/time formatting and parsing.
 * Provides formatters/parsers for b-datepicker, b-timepicker, b-datetimepicker.
 * Extracted from the removed w-datepicker / w-timepicker wrappers.
 */
export function useDateFormat() {
    /**
     * Format a Date object to YYYY-MM-DD string
     * For use with <b-datepicker :date-formatter="formatDate">
     */
    function formatDate(date) {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Parse a YYYY-MM-DD string to Date object
     * For use with <b-datepicker :date-parser="parseDate">
     */
    function parseDate(str) {
        if (!str) return null;
        const [year, month, day] = str.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    /**
     * Convert a model value to a Date object for b-datepicker / b-datetimepicker
     */
    function toDateValue(value) {
        if (!value) return null;
        if (value instanceof Date) return value;
        // Parse YYYY-MM-DD as local time to avoid UTC timezone shift
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [year, month, day] = value.split('-').map(Number);
            return new Date(year, month - 1, day);
        }
        return new Date(value);
    }

    /**
     * Convert a model value (string "HH:MM" or minutes number) to a Date for b-timepicker
     */
    function toTimeValue(value) {
        if (!value) return null;
        if (value instanceof Date) return value;

        // Handle time string format "HH:MM"
        if (typeof value === 'string' && value.includes(':')) {
            const [hours, minutes] = value.split(':').map(Number);
            const date = new Date();
            date.setHours(hours, minutes, 0, 0);
            return date;
        }

        // Handle numeric minutes
        if (typeof value === 'number' || !isNaN(Number(value))) {
            const totalMinutes = Number(value);
            const date = new Date();
            date.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);
            return date;
        }

        return new Date(value);
    }

    /**
     * Convert a Date from b-timepicker back to "HH:MM" string
     * For use with @update:model-value handler
     */
    function fromTimeValue(date) {
        if (!date) return '00:00';
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    }

    /**
     * Format a Date object to YYYY-MM-DD HH:MM string
     * For use with <b-datetimepicker :datetime-formatter="formatDateTime">
     */
    function formatDateTime(date) {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    }

    /**
     * Parse a YYYY-MM-DD HH:MM string to Date object
     * For use with <b-datetimepicker :datetime-parser="parseDateTime">
     */
    function parseDateTime(str) {
        if (!str) return null;
        const [datePart, timePart] = str.split(' ');
        if (!datePart) return null;
        const [year, month, day] = datePart.split('-').map(Number);
        let hours = 0,
            minutes = 0;
        if (timePart) {
            [hours, minutes] = timePart.split(':').map(Number);
        }
        return new Date(year, month - 1, day, hours, minutes);
    }

    /**
     * Convert a model value (string "YYYY-MM-DD HH:MM") to a Date object for b-datetimepicker
     */
    function toDateTimeValue(value) {
        if (!value) return null;
        if (value instanceof Date) return value;
        if (typeof value === 'string') {
            // ISO datetime (e.g. "2026-05-13T08:30:00.000000Z") — let Date parse + convert to local
            if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
                return new Date(value);
            }
            return parseDateTime(value);
        }
        return new Date(value);
    }

    return {
        formatDate,
        parseDate,
        toDateValue,
        formatDateTime,
        parseDateTime,
        toDateTimeValue,
        toTimeValue,
        fromTimeValue
    };
}
